import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { FaBolt, FaChartLine, FaClock } from 'react-icons/fa';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';
import {
  selectPeakSeasonDates,
  peakSeasonRangeLabel,
} from '../../utils/peakSeasonDates.js';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseCheckInDate(dateStr) {
  return new Date(`${dateStr}T00:00:00`);
}

function formatCheckInDate(dateStr) {
  const date = parseCheckInDate(dateStr);
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
    date: date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  };
}

function formatRelativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.max(0, Math.round((now - then) / 60000));
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}

// Honest scan-cadence copy that adapts to whatever the backend says. Avoids
// showing a misleading "5 min" when we're actually scanning every 16s.
function formatScanCadence(scanIntervalSeconds) {
  if (!scanIntervalSeconds || scanIntervalSeconds <= 0) {
    return null;
  }
  if (scanIntervalSeconds < 60) {
    return `every ${scanIntervalSeconds} sec`;
  }
  const minutes = Math.round(scanIntervalSeconds / 60);
  return `every ${minutes} min`;
}

// "How often a spot opens up", derived from the rolling total over the window.
// Adapts the unit so it reads naturally whether spots open every few minutes
// (busy outing) or every few days (quiet one). Returns null when there isn't
// enough to say anything meaningful.
function formatOpeningCadence(total, windowDays = 30) {
  if (!total || total <= 0) return null;
  const minutesPerOpening = (windowDays * 24 * 60) / total;
  if (minutesPerOpening < 90) {
    return `every ~${Math.max(1, Math.round(minutesPerOpening))} min`;
  }
  const hoursPerOpening = minutesPerOpening / 60;
  if (hoursPerOpening < 48) {
    return `every ~${Math.round(hoursPerOpening)} hr`;
  }
  const daysPerOpening = hoursPerOpening / 24;
  return `every ~${Math.round(daysPerOpening)} days`;
}

/**
 * ConfidencePanel - pre-purchase analytics modal.
 *
 * Reads state.news.analytics. The NewsBar owns the open/close state and the
 * trigger; this component is only the modal body. When analytics is missing or
 * empty, the parent should not be opening us in the first place, but we still
 * render a defensible empty state if invoked.
 */
export default function ConfidencePanel({ isOpen, onClose }) {
  const analytics = useUIStore((state) => state.news?.analytics, shallow);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={['sm', '3xl']} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx={3} maxH={['85vh', '85vh']}>
        <ModalHeader py={[3, 5]} px={[4, 8]} fontSize={['md', 'xl']}>
          <HStack spacing={2.5} align="center">
            <Icon as={FaChartLine} color="brand.600" boxSize={[4, 6]} />
            <Text noOfLines={1}>
              {analytics?.outingName
                ? `${analytics.outingName} stats`
                : 'Track record'}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={[5, 8]} px={[4, 8]}>
          <ConfidenceBody analytics={analytics} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function ConfidenceBody({ analytics }) {
  if (!analytics) {
    return (
      <Text fontSize="sm" color="gray.600">
        We haven&apos;t caught any cancellations for this trip yet. Stats will
        appear here once we do.
      </Text>
    );
  }

  const {
    last30d,
    scanIntervalSeconds,
    peakSeason,
  } = analytics;

  const hasAnyEvents = (last30d?.total ?? 0) > 0;

  return (
    <VStack spacing={[4, 6]} align="stretch">
      {hasAnyEvents && <HeroStats last30d={last30d} />}
      {hasAnyEvents && (
        <DatesWeCaught byDate={last30d?.byDate} peakSeason={peakSeason} />
      )}
      {hasAnyEvents && <DayOfWeekStrip byDayOfWeek={last30d.byDayOfWeek} />}
      <HonestyFooter scanIntervalSeconds={scanIntervalSeconds} />
    </VStack>
  );
}

function HeroStats({ last30d }) {
  const lastSeen = formatRelativeTime(last30d?.lastDetectedAt);
  const cadence = formatOpeningCadence(last30d?.total, 30);

  return (
    <SimpleGrid columns={[1, 3]} spacing={[2, 4]}>
      <StatTile
        icon={FaChartLine}
        label="Spots we caught"
        value={last30d.total.toLocaleString('en-US')}
        helper="in the last 30 days"
      />
      <StatTile
        icon={FaBolt}
        label="Last one we caught"
        value={lastSeen || '—'}
        helper="and counting"
      />
      <StatTile
        icon={FaClock}
        label="A spot opens up"
        value={cadence || '—'}
        helper="on average"
      />
    </SimpleGrid>
  );
}

function StatTile({ icon, label, value, helper }) {
  return (
    <Stat
      bg="brand.50"
      borderRadius="md"
      px={3}
      py={2.5}
      border="1px solid"
      borderColor="brand.100"
    >
      <HStack spacing={2} align="center" mb={0.5}>
        <Icon as={icon} color="brand.600" boxSize={3.5} />
        <StatLabel fontSize="xs" color="gray.600" m={0}>
          {label}
        </StatLabel>
      </HStack>
      <StatNumber
        fontSize={['lg', 'xl']}
        color="brand.700"
        fontWeight="bold"
        lineHeight="1.1"
      >
        {value}
      </StatNumber>
      <Text fontSize="xs" color="gray.500" mt={0.5}>
        {helper}
      </Text>
    </Stat>
  );
}

function DatesWeCaught({ byDate, peakSeason }) {
  const { dates, mode } = selectPeakSeasonDates(byDate, peakSeason);
  if (dates.length === 0) return null;

  const isPeakSeasonView =
    !!peakSeason && (mode === 'peak' || mode === 'shoulder');
  const seasonLabel = isPeakSeasonView ? peakSeasonRangeLabel(peakSeason) : null;
  const totalSpots = dates.reduce((sum, [, count]) => sum + count, 0);
  const heading = isPeakSeasonView
    ? 'Peak season dates we caught'
    : mode === 'recent'
    ? 'Dates we caught'
    : 'Upcoming dates we caught';

  return (
    <Box
      borderRadius="md"
      border="1px solid"
      borderColor="gray.100"
      px={3}
      py={3}
    >
      <HStack justify="space-between" align="baseline" mb={2} spacing={3}>
        <VStack align="start" spacing={0.5}>
          <Text fontSize={['sm', 'md']} fontWeight="bold" color="gray.800">
            {heading}
          </Text>
          {seasonLabel && (
            <Text fontSize="xs" color="gray.500">
              Peak season: {seasonLabel}
            </Text>
          )}
        </VStack>
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
          {totalSpots} {totalSpots === 1 ? 'spot' : 'spots'}
        </Text>
      </HStack>
      <SimpleGrid columns={[2, 3]} spacing={2}>
        {dates.map(([dateStr, count]) => {
          const { weekday, date } = formatCheckInDate(dateStr);
          return (
            <Box
              key={dateStr}
              bg="brand.50"
              border="1px solid"
              borderColor="brand.100"
              borderRadius="md"
              px={2}
              py={2}
              textAlign="center"
            >
              <Text fontSize="xs" color="gray.500" lineHeight="short">
                {weekday}
              </Text>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color="gray.800"
                lineHeight="short"
                mt={0.5}
              >
                {date}
              </Text>
              <Badge
                mt={1.5}
                colorScheme="brand"
                fontSize="xs"
                px={2}
                py={0.5}
                borderRadius="full"
              >
                {count} {count === 1 ? 'spot' : 'spots'}
              </Badge>
            </Box>
          );
        })}
      </SimpleGrid>
      <Text fontSize="xs" color="gray.400" mt={2}>
        {isPeakSeasonView
          ? 'Peak-season check-in dates where we detected openings · last 30 days'
          : 'Check-in dates where we detected openings · last 30 days'}
      </Text>
    </Box>
  );
}

function DayOfWeekStrip({ byDayOfWeek }) {
  if (!byDayOfWeek) return null;
  const values = Object.values(byDayOfWeek);
  const max = Math.max(1, ...values);
  const totalAcrossDays = values.reduce((sum, v) => sum + v, 0);
  if (totalAcrossDays === 0) return null;

  const busiest = Object.entries(byDayOfWeek).reduce(
    (best, [day, count]) => (count > best.count ? { day, count } : best),
    { day: null, count: -1 }
  );

  return (
    <Box>
      <HStack spacing={1.5} justify="space-between" align="baseline" mb={2}>
        <Text fontSize={['sm', 'md']} fontWeight="bold" color="gray.800">
          Which check-in days get cancelled
        </Text>
        {busiest.day && busiest.count > 0 && (
          <Text fontSize="xs" color="gray.500">
            busiest: <b>{busiest.day}</b>
          </Text>
        )}
      </HStack>
      <Flex justify="space-between" gap={1}>
        {DAY_LABELS.map((day) => {
          const count = byDayOfWeek[day] || 0;
          const intensity = count / max;
          return (
            <Tooltip
              key={day}
              label={`${count} cancellation${count === 1 ? '' : 's'} on ${day}s`}
              placement="top"
              hasArrow
            >
              <VStack
                flex={1}
                spacing={0.5}
                px={1}
                py={2}
                borderRadius="md"
                bg={intensity > 0 ? 'brand.500' : 'gray.100'}
                opacity={intensity > 0 ? 0.35 + intensity * 0.65 : 1}
                cursor="default"
                _hover={{ transform: 'translateY(-1px)' }}
                transition="transform 0.15s"
              >
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color={intensity > 0 ? 'white' : 'gray.500'}
                >
                  {day}
                </Text>
                <Text
                  fontSize="2xs"
                  color={intensity > 0 ? 'whiteAlpha.900' : 'gray.400'}
                >
                  {count}
                </Text>
              </VStack>
            </Tooltip>
          );
        })}
      </Flex>
    </Box>
  );
}

function HonestyFooter({ scanIntervalSeconds }) {
  const cadence = formatScanCadence(scanIntervalSeconds);

  return (
    <Box pt={3} borderTop="1px solid" borderColor="gray.100">
      <Text fontSize={['xs', 'sm']} color="gray.500" lineHeight="1.6" textAlign="center">
        {cadence && <>Scanning {cadence} from the live reservation system · </>}
        Past behavior isn&apos;t a guarantee.
      </Text>
    </Box>
  );
}
